import { invoke } from "@tauri-apps/api/core";
import { isTauri } from "@/lib/is-tauri";
import { isMacPlatform } from "@/lib/os-platform";
import {
  getSafariConnectionStatus,
  checkSafariConnection,
  type SafariConnectionStatus,
} from "./safari-connection";
import { configureSafariAccess, cancelSafariSetup } from "./safari-setup";
import {
  captureDesktopChatAuthScopeGuard,
  subscribeDesktopChatAuthScope,
} from "./desktop-chat-auth-scope";
import { recordBrowserConnectionObservation } from "./browser-connection-history";

type BrowserEntry = {
  /** For people, as Settings shows it; the desktop reports the same name. */
  name: string;
  /** Its macOS bundle identifier, lowercase. */
  bundle: string;
  /** Apple Events reach it on macOS: every browser whose app is signed. */
  appleEvents: boolean;
  /** The stable browser whose Apple Events setup a pre-release channel shares. */
  family?: "chrome" | "edge" | "brave";
};

/** Every browser Denker connects, as the desktop's catalog has them
 * (`companion_core/browser_catalog.rs`), in Settings' order: each
 * pre-release channel after its stable browser. */
const CATALOG = {
  safari: { name: "Safari", bundle: "com.apple.safari", appleEvents: true },
  chrome: { name: "Chrome", bundle: "com.google.chrome", appleEvents: true },
  chrome_beta: {
    name: "Chrome Beta",
    bundle: "com.google.chrome.beta",
    appleEvents: true,
    family: "chrome",
  },
  chrome_dev: {
    name: "Chrome Dev",
    bundle: "com.google.chrome.dev",
    appleEvents: true,
    family: "chrome",
  },
  chrome_canary: {
    name: "Chrome Canary",
    bundle: "com.google.chrome.canary",
    appleEvents: true,
    family: "chrome",
  },
  chromium: {
    name: "Chromium",
    bundle: "org.chromium.chromium",
    appleEvents: false,
  },
  edge: {
    name: "Microsoft Edge",
    bundle: "com.microsoft.edgemac",
    appleEvents: true,
  },
  edge_beta: {
    name: "Microsoft Edge Beta",
    bundle: "com.microsoft.edgemac.beta",
    appleEvents: true,
    family: "edge",
  },
  edge_dev: {
    name: "Microsoft Edge Dev",
    bundle: "com.microsoft.edgemac.dev",
    appleEvents: true,
    family: "edge",
  },
  edge_canary: {
    name: "Microsoft Edge Canary",
    bundle: "com.microsoft.edgemac.canary",
    appleEvents: true,
    family: "edge",
  },
  brave: { name: "Brave", bundle: "com.brave.browser", appleEvents: true },
  brave_beta: {
    name: "Brave Beta",
    bundle: "com.brave.browser.beta",
    appleEvents: true,
    family: "brave",
  },
  brave_nightly: {
    name: "Brave Nightly",
    bundle: "com.brave.browser.nightly",
    appleEvents: true,
    family: "brave",
  },
  arc: { name: "Arc", bundle: "company.thebrowser.browser", appleEvents: true },
  dia: { name: "Dia", bundle: "company.thebrowser.dia", appleEvents: true },
} satisfies Record<string, BrowserEntry>;
export type BrowserId = keyof typeof CATALOG;
export const BROWSERS: Readonly<Record<BrowserId, BrowserEntry>> = CATALOG;

/** Whether the browser is Chrome or one of its pre-release channels, which
 * share Chrome's required click on Allow JavaScript from Apple Events. */
export function isChromeFamily(browserId: BrowserId): boolean {
  return (BROWSERS[browserId].family ?? browserId) === "chrome";
}

/** Whether the browser is Edge or one of its channels, which install from
 * the Chrome Web Store once the person allows extensions from other stores. */
export function isEdgeFamily(browserId: BrowserId): boolean {
  return (BROWSERS[browserId].family ?? browserId) === "edge";
}
export type BrowserConnectionStatus = {
  browser_id: BrowserId;
  name: string;
  installed: boolean;
  automation: {
    status:
      | "granted"
      | "consent_required"
      | "denied"
      | "not_running"
      | "unavailable";
    code: number | null;
  };
  javascript: "not_checked" | "ready" | "unavailable";
  reason: string;
};
const SETUP_STATUSES = [
  "configured",
  "automation_required",
  "accessibility_required",
  "unsupported",
  "awaiting_user",
  "modal_window",
  "cancelled",
  "uncertain",
  "browser_not_running",
  "browser_busy",
] as const;
export type BrowserSetupReport = {
  browser_id: BrowserId;
  status: (typeof SETUP_STATUSES)[number];
  stage: string;
  developer_changed: boolean;
  javascript_changed: boolean;
  connection: BrowserConnectionStatus;
};
export type BrowserExternalAttempt = {
  setupId: string;
  report: BrowserSetupReport | null;
};
const REASONS = new Set([
  "not_installed",
  "not_checked",
  "automation_required",
  "automation_denied",
  "browser_not_running",
  "javascript_disabled",
  "launch_flag_required",
  "no_page",
  "probe_unavailable",
  "browser_busy",
  "unsupported",
  "disconnected",
  "ready",
]);

/** Apple Events connect browsers here - `browserId`, when given, among
 * them: on macOS, never to unsigned Chromium. */
export function browserConnectionApplies(browserId?: BrowserId): boolean {
  return (
    isTauri() &&
    isMacPlatform() &&
    (browserId === undefined || BROWSERS[browserId].appleEvents)
  );
}
export function isBrowserId(value: unknown): value is BrowserId {
  return (
    typeof value === "string" &&
    Object.prototype.hasOwnProperty.call(BROWSERS, value)
  );
}
export function parseBrowserConnectionStatus(
  value: unknown,
): BrowserConnectionStatus {
  if (!value || typeof value !== "object")
    throw new Error("Invalid browser status");
  const status = value as BrowserConnectionStatus;
  if (
    !isBrowserId(status.browser_id) ||
    status.name !== BROWSERS[status.browser_id].name ||
    typeof status.installed !== "boolean" ||
    !status.automation ||
    ![
      "granted",
      "consent_required",
      "denied",
      "not_running",
      "unavailable",
    ].includes(status.automation.status) ||
    !(
      status.automation.code === null ||
      Number.isInteger(status.automation.code)
    ) ||
    !["not_checked", "ready", "unavailable"].includes(status.javascript) ||
    !REASONS.has(status.reason) ||
    ((status.javascript === "ready" || status.reason === "ready") &&
      !(
        status.installed &&
        status.automation.status === "granted" &&
        status.javascript === "ready" &&
        status.reason === "ready"
      ))
  ) {
    throw new Error("Invalid browser status");
  }
  return status;
}
function ensureAvailable() {
  if (!browserConnectionApplies())
    throw new Error("Browser connection unavailable");
}
function safariStatus(status: SafariConnectionStatus): BrowserConnectionStatus {
  return {
    ...status,
    browser_id: "safari",
    name: "Safari",
    installed: true,
    reason:
      status.reason === "safari_not_running"
        ? "browser_not_running"
        : status.reason,
  };
}
async function read(
  command: string,
  browserId: BrowserId,
): Promise<BrowserConnectionStatus> {
  ensureAvailable();
  const status = parseBrowserConnectionStatus(
    await invoke<unknown>(command, { browserId }),
  );
  if (status.browser_id !== browserId)
    throw new Error("Browser status identity changed");
  return status;
}
/** Older signed builds still expose the verified Safari adapter. */
export function isMissingBrowserCatalog(error: unknown): boolean {
  const message =
    typeof error === "string"
      ? error
      : error instanceof Error
        ? error.message
        : "";
  return (
    /^Command [`"']?list_browser_connections[`"']? not found[.!]?$/i.test(
      message.trim(),
    ) ||
    /^Unknown command[: ]+[`"']?list_browser_connections[`"']?[.!]?$/i.test(
      message.trim(),
    )
  );
}
export async function listBrowserConnections(): Promise<
  BrowserConnectionStatus[]
> {
  ensureAvailable();
  const result = await invoke<unknown>("list_browser_connections");
  if (!Array.isArray(result)) throw new Error("Invalid browser list");
  const statuses = result.map(parseBrowserConnectionStatus);
  if (
    new Set(statuses.map((status) => status.browser_id)).size !==
    statuses.length
  ) {
    throw new Error("Duplicate browser status");
  }
  return statuses.map(passiveStatus);
}
function passiveStatus(
  status: BrowserConnectionStatus,
): BrowserConnectionStatus {
  return {
    ...status,
    javascript: "not_checked",
    reason: status.reason === "ready" ? "not_checked" : status.reason,
  };
}
/** Permission reads never imply current page access or trigger OS consent. */
export async function getBrowserConnectionStatus(
  browserId: BrowserId,
): Promise<BrowserConnectionStatus> {
  if (browserId === "safari")
    return safariStatus(await getSafariConnectionStatus());
  return passiveStatus(await read("get_browser_connection_status", browserId));
}
export async function checkBrowserConnection(
  browserId: BrowserId,
): Promise<BrowserConnectionStatus> {
  const current = captureDesktopChatAuthScopeGuard();
  const revision = accessRevisions.get(browserId);
  const status =
    browserId === "safari"
      ? safariStatus(await checkSafariConnection())
      : await read("check_browser_connection", browserId);
  if (current() && revision === accessRevisions.get(browserId))
    recordBrowserConnectionObservation(status);
  return status;
}

const inspections = new Map<BrowserId, Promise<BrowserConnectionStatus>>();
const accessRevisions = new Map<BrowserId, number>();
subscribeDesktopChatAuthScope(() => inspections.clear());

/** Explicit user intent is the only way to restore a disconnected browser. */
export async function setBrowserConnectionEnabled(
  browserId: BrowserId,
  enabled: boolean,
): Promise<BrowserConnectionStatus> {
  ensureAvailable();
  const current = captureDesktopChatAuthScopeGuard();
  accessRevisions.set(browserId, (accessRevisions.get(browserId) ?? 0) + 1);
  inspections.delete(browserId);
  const status = parseBrowserConnectionStatus(
    await invoke<unknown>("set_browser_connection_enabled", {
      browserId,
      enabled,
    }),
  );
  if (status.browser_id !== browserId)
    throw new Error("Browser status identity changed");
  if (current()) recordBrowserConnectionObservation(status);
  return status;
}

/** Permission evidence only, for reads nobody asked for (a mount, a focus):
 * the browser itself is never sent an Apple Event. */
export async function readBrowserConnection(
  browserId: BrowserId,
): Promise<BrowserConnectionStatus> {
  const current = captureDesktopChatAuthScopeGuard();
  const revision = accessRevisions.get(browserId);
  const status = await getBrowserConnectionStatus(browserId);
  if (current() && revision === accessRevisions.get(browserId))
    recordBrowserConnectionObservation(status);
  return status;
}

/** What only a page check finds, and a permission read cannot disprove. */
const PAGE_CHECK_FINDINGS = new Set([
  "ready",
  "javascript_disabled",
  "launch_flag_required",
  "no_page",
]);

/** A permission read cannot see the page: while permission still stands, the
 * last page check's finding stays instead of turning into "not checked". */
export function withLiveEvidence(
  previous: BrowserConnectionStatus | null | undefined,
  passive: BrowserConnectionStatus,
): BrowserConnectionStatus {
  return previous?.browser_id === passive.browser_id &&
    PAGE_CHECK_FINDINGS.has(previous.reason) &&
    passive.automation.status === "granted" &&
    passive.reason === "not_checked"
    ? previous
    : passive;
}

/** Inspect current access without requesting consent, launching or configuring:
 * once access is granted, a page check runs in the browser itself, so only a
 * check the person asked for or a setup in progress inspects
 * (`readBrowserConnection` otherwise). Share only an in-flight read; each
 * later inspection obtains fresh evidence. */
export function inspectBrowserConnection(
  browserId: BrowserId,
): Promise<BrowserConnectionStatus> {
  const pending = inspections.get(browserId);
  if (pending) return pending;
  const requireCurrent = () => {
    if (inspections.get(browserId) !== inspection)
      throw new Error("Browser inspection belongs to a previous sign-in.");
  };
  const inspection = getBrowserConnectionStatus(browserId)
    .then((status) => {
      requireCurrent();
      return status.installed &&
        status.automation.status === "granted" &&
        ![
          "not_installed",
          "browser_not_running",
          "unsupported",
          "disconnected",
        ].includes(status.reason)
        ? checkBrowserConnection(browserId)
        : status;
    })
    .then((status) => {
      requireCurrent();
      recordBrowserConnectionObservation(status);
      return status;
    })
    .finally(() => {
      if (inspections.get(browserId) === inspection)
        inspections.delete(browserId);
    });
  inspections.set(browserId, inspection);
  return inspection;
}
export async function configureBrowserConnection(
  browserId: BrowserId,
  setupId: string,
  restart = false,
): Promise<BrowserSetupReport> {
  ensureAvailable();
  const current = captureDesktopChatAuthScopeGuard();
  if (restart && browserId !== "dia")
    throw new Error("Restart approval is available only for Dia.");
  if (browserId === "safari") {
    const report = await configureSafariAccess(setupId);
    const result: BrowserSetupReport = {
      ...report,
      browser_id: "safari",
      stage: report.stage ?? "preflight",
      status:
        report.status === "safari_not_running"
          ? "browser_not_running"
          : report.status,
      connection: safariStatus(report.connection),
    };
    if (current()) recordBrowserConnectionObservation(result.connection);
    return result;
  }
  const value = await invoke<unknown>("configure_browser_connection", {
    browserId,
    setupId,
    ...(restart ? { restart: true } : {}),
  });
  const report = parseBrowserSetupReport(value, browserId);
  if (current()) recordBrowserConnectionObservation(report.connection);
  return report;
}
export function parseBrowserSetupReport(
  value: unknown,
  browserId: BrowserId,
): BrowserSetupReport {
  if (!value || typeof value !== "object")
    throw new Error("Invalid browser setup result");
  const report = value as BrowserSetupReport;
  if (
    report.browser_id !== browserId ||
    !SETUP_STATUSES.includes(report.status) ||
    typeof report.stage !== "string" ||
    !/^[a-z_]{1,80}$/.test(report.stage) ||
    typeof report.developer_changed !== "boolean" ||
    typeof report.javascript_changed !== "boolean"
  ) {
    throw new Error("Invalid browser setup result");
  }
  const connection = parseBrowserConnectionStatus(report.connection);
  if (
    connection.browser_id !== browserId ||
    (connection.javascript === "ready" && report.status !== "configured")
  ) {
    throw new Error("Invalid browser setup result");
  }
  return { ...report, connection };
}

export async function cancelBrowserConnectionSetup(
  browserId: BrowserId,
  setupId: string,
): Promise<void> {
  ensureAvailable();
  if (browserId === "safari") return cancelSafariSetup(setupId);
  await invoke("cancel_browser_connection_setup", { browserId, setupId });
}
/** Guidance gave up: an agent waiting on this setup returns for the person. */
export async function handOffBrowserConnectionSetup(
  browserId: BrowserId,
  setupId: string,
): Promise<void> {
  ensureAvailable();
  await invoke("hand_off_browser_connection_setup", { browserId, setupId });
}
